import * as angular from 'angular';
import * as moment from 'moment';

import { TagDefinition, SeriesDefinition, TagDateValue, IStatistics, IValueViewModel, IdentifierData, ActualDataType, DigitalSetSimpleDictionary, Dictionary, AggregationMode, Group, TagStringValueDto } from '../../models';
import { ITagsTrendService, ISeriesService, IDigitalSetHelperService, IFormatNumberHelper, IdentifierDetailsService, IStringValuesHelperService } from '../../services';
import { errorTextColor, infoTextColor, warningTextColor } from '../../common/constants';

export interface ITagPageService {
    createWidgets(tag: TagDefinition): { gauge };
    createSeries(tagDefinition: TagDefinition): SeriesDefinition<TagDateValue>[];
    read(guid: string, beginDate: string, endDate: string, value?: string, force?: boolean): angular.IPromise<{ values: TagDateValue[]; missing?: any; compressed: boolean }>;
    loadMinMax(tagDefinition: TagDefinition, vm: IValueViewModel, groups: Dictionary<Group>): angular.IPromise<{ value: number; color: string }[]>;
    prepareData(data: { values: TagDateValue[]; missing?: any }, beginDate: string, endDate: string, digitalSets: DigitalSetSimpleDictionary, viewModels: Dictionary<IValueViewModel>, stringValues?: TagStringValueDto[]): { stats: IStatistics; values: TagDateValue[] };
    horizontalLines(vm: IValueViewModel): { value: number; color: string; name?: string }[];
    readData(guid: string, beginDate: string, endDate: string, valueType: string, digitalSets: DigitalSetSimpleDictionary, viewModels: Dictionary<IValueViewModel>, force: boolean, stringValues?: TagStringValueDto[]): angular.IPromise<IdentifierData>;
}

enum TagStatus {
    OK,
    OKWithErrors,
    OutOfRange,
    OverRange,
    UnderRange,
    Error,
    ServerError,
    NotFound,
    Unknown,
    NoData
}

const warnStatuses = [TagStatus.OKWithErrors, TagStatus.OutOfRange, TagStatus.OverRange, TagStatus.UnderRange];
const errorStatuses = [TagStatus.Error, TagStatus.ServerError];

export class TagPageService implements ITagPageService {
    static serviceId: string = 'tagPageService';

    static $inject = [
        'tagsTrendService',
        'seriesService',
        'formatNumberHelper',
        'identifierDetailsService',
        'digitalSetHelperService',
        'stringValuesHelperService'
    ];

    constructor(
        private _tagsTrendService: ITagsTrendService,
        private _seriesService: ISeriesService,
        private _formatNumberHelper: IFormatNumberHelper,
        private _identifierDetailsService: IdentifierDetailsService,
        private _digitalSetHelperService: IDigitalSetHelperService,
        private _stringValuesHelperService: IStringValuesHelperService
    ) {
    }

    createWidgets(tag: TagDefinition): { gauge } {
        const result: { gauge } = {
            gauge: this.createGauge(tag.guid)
        };
        return result;
    }

    private createGauge(guid) {
        const item = {
            sizeX: 3,
            sizeY: 6,
            col: 0,
            row: 6,
            name: 'Data quality',
            type: '',
            configuration: { guid: guid, type: 'tag' }
        };
        return item;
    }

    createSeries(tagDefinition: TagDefinition): SeriesDefinition<TagDateValue>[] {
        const isDigital = tagDefinition.actualDataType === ActualDataType.Digital;
        const isString = tagDefinition.actualDataType === ActualDataType.String;
        const crosshairFormat = (isDigital || isString)
            ? d => d.comment
            : d => this._formatNumberHelper.getFormatter(2).format(d.value);
        const series: SeriesDefinition<TagDateValue>[] = [];
        series.push({
            name: tagDefinition.name + ' (raw)',
            identifier: tagDefinition.guid + 'raw',
            value: d => d.value,
            selector: d => d.guid + d.valueType,
            color: 'rgba(204, 204, 204, 0.7)',
            isLineVisible: false,
            isSymbolVisible: true,
            serieState: 0,
            symbol: 'circle',
            isCrosshairHidden: false,
            isVisible: false,
            crosshairFormat: crosshairFormat,
            coloringConditions: [
                {
                    color: 'rgba(255, 153, 51, 0.7)',
                    condition: (d) => warnStatuses.indexOf(d.secondaryValue) >= 0
                },
                {
                    color: 'rgba(255, 0, 0, 0.7)',
                    condition: (d) => errorStatuses.indexOf(d.secondaryValue) >= 0
                }
            ]
        });
        series.push({
            name: tagDefinition.name,
            identifier: tagDefinition.guid + ((isDigital || isString) ? 'interpolated' : 'average'),
            value: d => d.value,
            selector: d => d.guid + d.valueType,
            color: infoTextColor,
            symbol: 'circle',
            isLineVisible: false,
            isSymbolVisible: true,
            serieState: 0,
            crosshairFormat: crosshairFormat
        });
        series.push(this.createAverageSeries());
        series.push(this.createGradientSeries());
        series.push(this.createStdDevSeries());
        series.push(this._seriesService.createMissingSerie());
        return series;
    }

    horizontalLines(vm: IValueViewModel): { value: number; color: string; name?: string }[] {
        const horizontalLines = [];
        if (vm.target !== null && vm.target !== undefined) {
            horizontalLines.push({
                value: vm.target,
                color: '#fc3',
                name: 'target'
            });
        }
        if (vm.min !== null && vm.min !== undefined) {
            horizontalLines.push({
                value: vm.min,
                color: '#939',
                name: 'min'
            });
        }
        if (vm.max !== null && vm.max !== undefined) {
            horizontalLines.push({
                value: vm.max,
                color: '#939',
                name: 'max'
            });
        }
        return horizontalLines;
    }
    private createAverageSeries() {
        const identifier = 'average';
        const displayName = 'Average';
        const series = {
            name: displayName,
            guid: 'stats',
            identifier: `stats${identifier}`,
            selector: d => d.name + identifier,
            value: d => d.average,
            color: '#fc3',
            isLineVisible: true,
            isSymbolVisible: false,
            isVisible: false,
            pane: 0,
            isCrosshairHidden: true
        };
        return series;
    }

    private createStdDevSeries(): any {
        const identifier = 'standardDeviation';
        const displayName = 'Standard deviation';
        const series = {
            name: displayName,
            guid: 'stats',
            identifier: `stats${identifier}`,
            selector: d => d.name + `${identifier}`,
            color: '#c9c',
            seriesType: 'area',
            y0: d => d.average - d.stdDev,
            y1: d => d.average + d.stdDev,
            isVisible: false,
            pane: 0,
            isCrosshairHidden: true
        };
        return series;
    }

    private createGradientSeries() {
        const identifier = 'gradient';
        const displayName = 'Gradient';
        const series = {
            name: displayName,
            guid: 'stats',
            identifier: `stats${identifier}`,
            selector: d => d.name + identifier,
            value: (d) => d.gradient,
            color: '#cc0',
            isLineVisible: true,
            isSymbolVisible: false,
            isVisible: false,
            pane: 0,
            isCrosshairHidden: true
        };
        return series;
    }

    read(guid: string, beginDate: string, endDate: string, value: string = 'average', force: boolean = false):
        angular.IPromise<{ values: TagDateValue[]; missing: any; compressed: boolean }> {
        return this._tagsTrendService
            .getReadPromise([{ guid, value: value }], moment(beginDate), moment(endDate), false, force)
            .then((data) => {
                return {
                    values: data.values,
                    missing: data.missing,
                    compressed: data.compressed
                };
            });
    }

    prepareData(
        data: { values: TagDateValue[]; missing?: any },
        beginDate: string,
        endDate: string,
        digitalSets: DigitalSetSimpleDictionary,
        viewModels: Dictionary<IValueViewModel>,
        stringValues?: TagStringValueDto[]
    ): { stats: IStatistics; values: TagDateValue[] } {
        const preparedData = this._identifierDetailsService.prepareData(data, beginDate, endDate);
        preparedData.values = this._digitalSetHelperService.mapDigitalSetsForTagDatesValues(preparedData.values, digitalSets, viewModels, AggregationMode.None);
        preparedData.values = this._stringValuesHelperService.mapStringForTagDatesValues(preparedData.values, stringValues, viewModels, AggregationMode.None);
        return preparedData;
    }

    loadMinMax(tagDefinition: TagDefinition, vm: IValueViewModel, groups: Dictionary<Group>): angular.IPromise<{ value: number; color: string }[]> {
        return this._seriesService
            .getMinMax(tagDefinition, vm, groups)
            .then(data => {
                const values = [];
                if (data.min || data.min === 0) {
                    values.push(data.min);
                }
                if (data.max || data.max === 0) {
                    values.push(data.max);
                }
                const colors = this.getColorArray(values);

                if (data.defaultValue || data.defaultValue === 0) {
                    colors.push({ value: data.defaultValue, color: warningTextColor });
                }
                return colors;
            });
    }

    readData(guid: string, beginDate: string, endDate: string, valueType: string, digitalSets: DigitalSetSimpleDictionary, viewModels: Dictionary<IValueViewModel>, force: boolean, stringValues?: TagStringValueDto[]): angular.IPromise<IdentifierData> {
        return this
            .read(guid, beginDate, endDate, valueType, force)
            .then(data => {
                return data.compressed
                    ? this._tagsTrendService.read([{ guid, value: valueType }], beginDate, endDate, false, force)
                    : data;
            })
            .then(data => {
                const prepareData = this.prepareData(data, beginDate, endDate, digitalSets, viewModels, stringValues);
                const results: IdentifierData = {
                    preparedData: prepareData.values,
                    statsData: {
                        average: prepareData.stats.average,
                        standardDeviation: prepareData.stats.stdDev,
                        gradient: prepareData.stats.slope
                    }
                };
                return results;
            });
    }

    private getColorVal(value: number): { value: number; color: string } {
        return { value: value, color: '#939' };
    }

    private getColorArray(values: number[]) {
        return values.map(v => this.getColorVal(v));
    }
}